/* eslint-disable no-param-reassign */
/* eslint-disable react/no-danger */
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Edit as EditIcon } from "@material-ui/icons";
import Select from "react-select";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import BackLink from "../shared/BackLink";
import alertService from "../../services/alerts.service";

/**
 * Safely renders alert content without using dangerouslySetInnerHTML
 * @param {object} alert - The alert object
 * @returns {React.ReactNode} - The rendered content
 */
const renderAlertContent = alert => {
  // First determine which content to display
  const content = alert.description || alert.message || alert.msg || "";

  // If there's an image, render it safely
  if (alert.image) {
    return (
      <>
        <Typography variant="body2">
          {" "}
          <div
            dangerouslySetInnerHTML={{
              __html: content,
            }}
          />
        </Typography>
        <Box mt={1}>
          <img
            src={alert.image}
            alt={alert.title || "Alert image"}
            style={{ maxWidth: "100%", borderRadius: "4px" }}
          />
        </Box>
      </>
    );
  }

  // Return content as plain text with line breaks preserved
  return (
    <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
      <div
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />
    </Typography>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
  },
  filterContainer: {
    marginBottom: theme.spacing(4),
  },
  filterTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  card: {
    height: "100%",
    borderRadius: theme.spacing(1),
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  cardTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  alertContent: {
    wordBreak: "break-word",
  },
  chip: {
    marginTop: theme.spacing(2),
    fontWeight: 500,
  },
  activeChip: {
    backgroundColor: "#e6f7ee",
    color: "#099c59",
  },
  inactiveChip: {
    backgroundColor: "#fceaea",
    color: "#e53e3e",
  },
  editButton: {
    marginTop: theme.spacing(2),
    textTransform: "none",
    borderRadius: theme.spacing(0.75),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(8),
  },
}));

/**
 * The AlertsList component renders a list of alerts for the current user.
 * The user can filter the alerts by active status.
 *
 * The component fetches the alerts data from the database when mounted.
 * It renders a loading indicator until the data is loaded.
 * Then it renders a list of cards, each containing an alert with its title,
 * description, and active status.
 *
 * The user can edit an alert by clicking on the Edit button.
 *
 * @return {React.Component} The AlertsList component.
 */
export default function AlertsList() {
  const classes = useStyles();
  const { currentUser } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [immutableAlerts, setImmutableAlerts] = useState([]);
  const [active, setActive] = useState(true); // Default to showing active alerts
  const [loading, setLoading] = useState(true);

  const filterOptions = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
    { label: "All", value: null },
  ];

  // useEffect to fetch and set alerts data on component mount.
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const alertsData = await alertService.getUserAlerts(currentUser.id);
        if (alertsData && alertsData.length > 0) {
          setImmutableAlerts(alertsData);

          // Filter alerts based on initial active state (true)
          const activeAlerts = alertsData.filter(
            alert => alert.active === true
          );
          setAlerts(activeAlerts);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching alerts:", error);
        setLoading(false);
      }
    };

    if (currentUser?.id) {
      fetchAlerts();
    }
  }, [currentUser]);

  // useEffect to filter alerts based on the active state.
  useEffect(() => {
    if (!loading && immutableAlerts.length > 0) {
      let filteredAlerts = [...immutableAlerts];

      if (active === true) {
        filteredAlerts = filteredAlerts.filter(alert => alert.active === true);
      } else if (active === false) {
        filteredAlerts = filteredAlerts.filter(alert => alert.active === false);
      }

      setAlerts(filteredAlerts);
    }
  }, [active, immutableAlerts, loading]);

  return (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.title}>
        Alerts
      </Typography>
      <BackLink />

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <Box>
          {/* Filters */}
          <Paper elevation={0} className={classes.filterContainer}>
            <Box p={3}>
              <Typography variant="h6" className={classes.filterTitle}>
                Filters
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Select
                    placeholder="Status"
                    name="active"
                    options={filterOptions}
                    defaultValue={filterOptions[0]}
                    onChange={e => {
                      setActive(e.value);
                    }}
                    styles={{
                      control: base => ({
                        ...base,
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#90caf9",
                        },
                      }),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Box mt={4}>
            <Grid container spacing={3}>
              {alerts && alerts.length > 0 ? (
                alerts.map(alert => (
                  <Grid item lg={4} md={6} xs={12} key={alert.id}>
                    <Card className={classes.card} elevation={1}>
                      <CardContent className={classes.cardContent}>
                        <Typography
                          variant="h6"
                          className={classes.cardTitle}
                          gutterBottom
                        >
                          {alert.title || "Alert"}
                        </Typography>
                        <Divider />
                        <Box my={2}>
                          <Typography
                            variant="body2"
                            component="div"
                            className={classes.alertContent}
                          >
                            {renderAlertContent(alert)}
                          </Typography>
                        </Box>
                        <Chip
                          label={alert.active ? "Active" : "Inactive"}
                          size="small"
                          className={`${classes.chip} ${
                            alert.active
                              ? classes.activeChip
                              : classes.inactiveChip
                          }`}
                        />
                        <Box mt={2}>
                          <Button
                            component={Link}
                            to={`/alerts/${alert.id}/edit`}
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            className={classes.editButton}
                            fullWidth
                          >
                            Edit Alert
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Paper className={classes.emptyState} elevation={0}>
                    <Typography variant="body1" color="textSecondary">
                      No alerts found
                    </Typography>
                    <Box mt={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to="/alerts/new"
                      >
                        Create New Alert
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      )}
    </div>
  );
}
